// Main Layout Component - Responsive with collapsible sidebar
import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
// import AsistenteChat from './AsistenteChat'; // Asistente IA ocultado por falta de uso (jul-2026); el código se conserva
import ValidacionComercialAlfonso from './ValidacionComercialAlfonso';
import AvisoPedidosCortos from './AvisoPedidosCortos';
import GuiaInteractiva from './guia/GuiaInteractiva';
import { Menu, X, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useVistaEmpleado, ALTO_FRANJA_SIMULACION } from '../context/VistaEmpleadoContext';

const Layout = ({ children }) => {
  const { simulando, vistaEmpleado, salir } = useVistaEmpleado();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarDesktopCollapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleDesktopCollapsed = () => {
    setDesktopCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebarDesktopCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  return (
    <div
      className="flex h-[100dvh] bg-gray-50"
      style={simulando ? { paddingTop: ALTO_FRANJA_SIMULACION } : undefined}
    >
      {/* Franja de la vista de simulación (también en móvil) */}
      {simulando && (
        <div
          className="fixed top-0 inset-x-0 z-[70] bg-amber-400 text-amber-950 flex items-center justify-between gap-2 px-3 text-xs sm:text-sm"
          style={{ height: ALTO_FRANJA_SIMULACION }}
          role="status"
          data-testid="franja-simulacion"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <Eye className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="truncate">
              Viendo el ERP como <strong>{vistaEmpleado?.nombre}</strong>{vistaEmpleado?.rol ? ` (${vistaEmpleado.rol})` : ''} · solo lectura
            </span>
          </span>
          <button
            type="button"
            onClick={salir}
            className="shrink-0 h-6 px-2.5 rounded-md bg-amber-950 text-white text-xs font-semibold hover:bg-amber-900"
            data-testid="franja-simulacion-salir"
          >
            Salir
          </button>
        </div>
      )}

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - Fixed en desktop, slide-in en mobile (bajo la franja si se simula) */}
      <div
        style={simulando ? { top: ALTO_FRANJA_SIMULACION } : undefined}
        className={`
        ${isMobile
          ? `fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          : `fixed inset-y-0 left-0 z-30 transform transition-transform duration-300 ease-in-out ${desktopCollapsed ? '-translate-x-full' : 'translate-x-0'}`
        }
      `}>
        <Sidebar onNavigate={closeSidebar} isMobile={isMobile} />
      </div>

      {/* Toggle para colapsar el sidebar en desktop */}
      {!isMobile && (
        <button
          onClick={toggleDesktopCollapsed}
          className={`fixed top-1/2 -translate-y-1/2 z-40 w-6 h-12 rounded-r-lg bg-white border border-l-0 border-gray-200 shadow hover:bg-gray-50 flex items-center justify-center text-gray-500 hover:text-duetto-primary transition-all duration-300 ${desktopCollapsed ? 'left-0' : 'left-64'}`}
          title={desktopCollapsed ? 'Mostrar menú' : 'Ocultar menú'}
          aria-label="Toggle menu"
          data-tour="menu-mostrar"
        >
          {desktopCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}

      {/* Main content */}
      <main className={`flex-1 overflow-y-auto w-full transition-all duration-300 ${!isMobile && !desktopCollapsed ? 'ml-64' : 'ml-0'}`}>
        {/* Mobile header with hamburger */}
        {isMobile && (
          <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-700"
              aria-label="Toggle menu"
              data-tour="menu-abrir"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center justify-center flex-1">
              <img
                src="/duetto-logo-negro.png"
                alt="Duetto Catering"
                className="h-8 object-contain"
              />
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        )}

        <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
          {children}
        </div>
      </main>

      {/* Asistente IA ocultado por falta de uso (jul-2026); el código se conserva */}
      {/* <AsistenteChat /> */}

      {/* Validación comercial pre-facturación (solo Alfonso) */}
      <ValidacionComercialAlfonso />

      {/* Compras: pedidos ya hechos que se han quedado cortos (dirección) */}
      <AvisoPedidosCortos />

      {/* Guía interactiva de bienvenida (estado en GuiaContext; sobrevive a los cambios de ruta) */}
      <GuiaInteractiva
        isMobile={isMobile}
        menuAbierto={sidebarOpen}
        menuOculto={!isMobile && desktopCollapsed}
        cerrarMenu={closeSidebar}
      />
    </div>
  );
};

export default Layout;
